import { useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { API, authed, assetUrl } from "@/lib/api";
import { HEADING_FONTS, BODY_FONTS } from "@/context/ConfigContext";

export const inputCls = "w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 ring-brand transition-shadow";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-stone-700">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const Section = ({ title, hint, children }) => (
  <section className="bg-white rounded-2xl border p-5 sm:p-6">
    <h2 className="text-lg font-semibold">{title}</h2>
    {hint && <p className="text-sm text-stone-500 mb-4">{hint}</p>}
    <div className="space-y-4 mt-3">{children}</div>
  </section>
);

const RemoveBtn = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} className="p-2 text-stone-400 hover:text-red-600" data-testid={testid}><Trash2 className="w-4 h-4" /></button>
);
export const AddBtn = ({ onClick, label, testid }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-2 text-sm font-medium text-brand" data-testid={testid}><Plus className="w-4 h-4" /> {label}</button>
);

export function useUpload(schoolId) {
  const { getToken } = useAuth();
  return async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/schools/${schoolId}/upload?kind=${kind}`, fd, await authed(getToken));
    return data.url;
  };
}

export function ImageField({ label, value, onChange, kind, schoolId, testid }) {
  const upload = useUpload(schoolId);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      onChange(await upload(file, kind));
      toast.success("Image uploaded — click Save to publish it");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <Field label={label}>
      <div className="flex flex-wrap items-center gap-3">
        <div className={`${kind === "banner" ? "w-28" : "w-16"} h-16 rounded-xl border bg-stone-50 flex items-center justify-center overflow-hidden`} data-testid={`${testid}-preview`}>
          {value ? <img src={assetUrl(value)} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-stone-400">None</span>}
        </div>
        <span className="relative flex items-center gap-2 px-4 py-2 rounded-full border text-sm cursor-pointer hover:bg-stone-50" data-testid={`${testid}-upload-label`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={pick} data-testid={`${testid}-upload-input`} />
        </span>
        {value && <button type="button" onClick={() => onChange("")} className="text-sm text-red-600" data-testid={`${testid}-remove`}>Remove</button>}
      </div>
      <input className={`${inputCls} mt-3`} placeholder="…or paste an image link (https://…)" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url-input`} />
    </Field>
  );
}

const FontSelect = ({ label, value, fonts, onChange, testid }) => (
  <Field label={label}>
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} style={{ fontFamily: value }} data-testid={testid}>
      {fonts.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
    </select>
  </Field>
);

export const BrandingSection = ({ form, set }) => (
  <Section title="Branding & typography" hint="Colours and fonts preview live while you edit.">
    <Field label="Chat assistant name"><input className={inputCls} value={form.chatbot_name} onChange={(e) => set("chatbot_name", e.target.value)} data-testid="config-input-chatbot-name" /></Field>
    <div className="grid grid-cols-2 gap-4">
      {[["primary_color", "Primary colour", "config-input-primary-color"], ["accent_color", "Accent colour", "config-input-accent-color"]].map(([k, l, tid]) => (
        <Field key={k} label={l}>
          <div className="flex items-center gap-2">
            <input type="color" value={form[k]} onChange={(e) => set(k, e.target.value)} className="w-11 h-11 rounded-lg border cursor-pointer" data-testid={tid} />
            <input className={inputCls} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`${tid}-hex`} />
          </div>
        </Field>
      ))}
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <FontSelect label="Heading font" value={form.heading_font} fonts={HEADING_FONTS} onChange={(v) => set("heading_font", v)} testid="config-heading-font-select" />
      <FontSelect label="Body font" value={form.body_font} fonts={BODY_FONTS} onChange={(v) => set("body_font", v)} testid="config-body-font-select" />
    </div>
  </Section>
);

export const ContentSection = ({ form, set }) => (
  <Section title="About & curriculum">
    <Field label="About section title"><input className={inputCls} value={form.about_title} onChange={(e) => set("about_title", e.target.value)} data-testid="config-input-about-title" /></Field>
    <Field label="About us"><textarea rows={4} className={inputCls} value={form.about_text} onChange={(e) => set("about_text", e.target.value)} data-testid="config-input-about-text" /></Field>
    <Field label="Programmes & curriculum"><textarea rows={3} className={inputCls} value={form.curriculum} onChange={(e) => set("curriculum", e.target.value)} data-testid="config-input-curriculum" /></Field>
  </Section>
);

export const FacilitiesSection = ({ form, set }) => {
  const edit = (i, k, v) => set("facilities", form.facilities.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <Section title="Facilities & activities">
      {form.facilities.map((f, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`config-facility-row-${i}`}>
          <input className={`${inputCls} col-span-12 sm:col-span-4`} placeholder="Facility" value={f.title} onChange={(e) => edit(i, "title", e.target.value)} data-testid={`config-facility-title-${i}`} />
          <input className={`${inputCls} col-span-11 sm:col-span-7`} placeholder="Short description" value={f.description} onChange={(e) => edit(i, "description", e.target.value)} data-testid={`config-facility-desc-${i}`} />
          <div className="col-span-1"><RemoveBtn onClick={() => set("facilities", form.facilities.filter((_, j) => j !== i))} testid={`config-facility-remove-${i}`} /></div>
        </div>
      ))}
      <AddBtn onClick={() => set("facilities", [...form.facilities, { title: "", description: "" }])} label="Add facility" testid="config-add-facility-button" />
      <Field label="Activities (one per line)">
        <textarea rows={4} className={inputCls} value={form.activities.join("\n")} onChange={(e) => set("activities", e.target.value.split("\n"))} data-testid="config-input-activities" />
      </Field>
    </Section>
  );
};

export const GallerySection = ({ form, set, schoolId }) => (
  <Section title="Photo gallery" hint="Upload photos of classrooms, events and activities.">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {form.gallery.map((g, i) => (
        <div key={g + i} className="relative group" data-testid={`config-gallery-item-${i}`}>
          <img src={assetUrl(g)} alt="" className="w-full h-28 object-cover rounded-xl border" />
          <button type="button" onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))}
            className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/90 text-red-600 shadow" data-testid={`config-gallery-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
    <ImageField label="Add a photo" value="" kind="gallery" schoolId={schoolId} testid="config-gallery"
      onChange={(url) => url && set("gallery", [...form.gallery, url])} />
  </Section>
);

export const TestimonialsSection = ({ form, set }) => {
  const edit = (i, k, v) => set("testimonials", form.testimonials.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <Section title="Testimonials">
      {form.testimonials.map((t, i) => (
        <div key={i} className="rounded-xl border p-3 space-y-2" data-testid={`config-testimonial-row-${i}`}>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="Parent name" value={t.name} onChange={(e) => edit(i, "name", e.target.value)} data-testid={`config-testimonial-name-${i}`} />
            <input className={inputCls} placeholder="e.g. Parent of Aarav, Nursery" value={t.role} onChange={(e) => edit(i, "role", e.target.value)} data-testid={`config-testimonial-role-${i}`} />
            <RemoveBtn onClick={() => set("testimonials", form.testimonials.filter((_, j) => j !== i))} testid={`config-testimonial-remove-${i}`} />
          </div>
          <textarea rows={2} className={inputCls} placeholder="What they said" value={t.text} onChange={(e) => edit(i, "text", e.target.value)} data-testid={`config-testimonial-text-${i}`} />
        </div>
      ))}
      <AddBtn onClick={() => set("testimonials", [...form.testimonials, { name: "", role: "", text: "" }])} label="Add testimonial" testid="config-add-testimonial-button" />
    </Section>
  );
};
